import { Injectable } from '@angular/core';
import { DataService } from '../data.service';
import { BehaviorSubject, Observable, map, of, tap } from 'rxjs';
import { GrandOtta, GrandOttaCateg } from '../models/grand-otta';
import { AppService, DbCallError } from '../app.service';
import moment from 'moment-timezone';
import { TimesheetService } from '../ts/timesheet.service';
import { ReportTimesheetSummaryResponse } from '../models/timesheet-fees';
import { TimesheetBaseParsed } from '../models/timesheet';
import { UserService } from '../user.service';

@Injectable({
  providedIn: 'root'
})
export class ReportService {
  otta$ = new BehaviorSubject<GrandOtta | null>(null)
  isGrandOttaLoading$ = new BehaviorSubject<boolean>(false)

  weeklyOtta$ = new BehaviorSubject<GrandOtta | null>(null)
  isWeeklyOttasLoading$ = new BehaviorSubject<boolean>(false)
  weeklyOttasError$ = new BehaviorSubject<DbCallError>(null)

  constructor(
    private dataSvc: DataService,
    private appSvc: AppService,
    private tsSvc: TimesheetService,
    private userSvc: UserService,
    ) { }

  getGrandOtta() {
    this.isGrandOttaLoading$.next(true)
    const retVal = this.dataSvc.getGrandOtta()
    retVal.subscribe({
      next: otta => {
        this.isGrandOttaLoading$.next(false)
        this.otta$.next(otta);
      },
      error: err => {
        this.isGrandOttaLoading$.next(false)
      }
    })
    return retVal
  }

  getWeeklyOttas(from: moment.Moment, to: moment.Moment) {
    //console.log(from, to)
    this.isWeeklyOttasLoading$.next(true)
    const retVal = this.dataSvc.getGrandOtta(GrandOttaCateg.Weekly, from, to)
    retVal
      .pipe(
        tap(() => this.isWeeklyOttasLoading$.next(false))
      )
      .subscribe({
        next: otta => {
          const allOttaPeriods = this.weeklyOtta$.value
            ? this.appSvc.replaceDocItems(otta.periods, this.weeklyOtta$.value.periods, 'period')
            : null
          if (allOttaPeriods) {
            otta.periods = allOttaPeriods
          }
          otta.periods.sort((p1, p2) => p1.period.localeCompare(p2.period))
          this.weeklyOtta$.next(otta)
          this.weeklyOttasError$.next(false)
        },
        error: err => {
          this.weeklyOttasError$.next(err)
        }
      })
    return retVal
  }

  getDailyOttasForWeek(startdate: moment.Moment) {
    const enddate = startdate.clone().add(1, 'week');
    return this.dataSvc.getGrandOtta(GrandOttaCateg.Daily, startdate, enddate);
  }

  isSalaryReportLoading$ = new BehaviorSubject<boolean>(false)
  salaryReportError$ = new BehaviorSubject<DbCallError>(null)
  getSalaryReport(startDate?: Date | null, endDate?: Date | null, usid?: number, depid?: number): Observable<Partial<TimesheetBaseParsed>[] | null> {
    this.isSalaryReportLoading$.next(true)
    this.salaryReportError$.next(null)
    const retVal = this.dataSvc.getSalaryReport(moment(startDate), moment(endDate), usid, depid)
      .pipe(
        map(this.tsSvc.parseSalaryReport)
    )
    retVal.subscribe({
      next: () => {
        this.isSalaryReportLoading$.next(false)
      },
      error: err => {
        this.isSalaryReportLoading$.next(false)
        this.salaryReportError$.next(err)
      }
    })
    return retVal
  }

  isTimesheetSummaryReportLoading$ = new BehaviorSubject<boolean>(false)
  timesheetSummaryReportError$ = new BehaviorSubject<DbCallError>(null)
  getTimesheetSummaryReport(startDate?: moment.Moment | null, endDate?: moment.Moment | null, contractType?: 'ekho' | 'hun_company' | null): Observable<ReportTimesheetSummaryResponse[]> {
    this.isTimesheetSummaryReportLoading$.next(true)
    this.timesheetSummaryReportError$.next(null)
    const retVal = this.dataSvc.getTimesheetSummaryReport(startDate, endDate, contractType)
      .pipe(
        map(data => data.map(x => ({
          ...x,
          dMinStartDate: new Date(x.minstartdate),
          dMaxStartDate: new Date(x.maxstartdate),
          usname: this.userSvc.getFullName(x),
        })))
      )
    retVal.subscribe({
      next: () => {
        this.isTimesheetSummaryReportLoading$.next(false)
      },
      error: err => {
        this.isTimesheetSummaryReportLoading$.next(false)
        this.timesheetSummaryReportError$.next(err)
      }
    })
    return retVal
  }
}
